/**
 * FoodPulseGuideDetail — cornerstone guide reader.
 *
 * Ships every guide with:
 *  - Breadcrumb (BreadcrumbList JSON-LD)
 *  - Journey pill + status badge + last-reviewed date
 *  - Rich body: summary + sections
 *  - Sources block (transparent provenance — audit requirement)
 *  - Related guides
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BookOpen, Loader2, ExternalLink, BadgeCheck } from "lucide-react";
import { http } from "@/lib/api";
import SeoHead from "@/components/SeoHead";
import FoodPulseTopNav from "@/components/FoodPulseTopNav";
import FoodPulseJourneyNav from "@/components/FoodPulseJourneyNav";
import FeatureStatusBadge from "@/components/FeatureStatusBadge";
import MethodologyDisclaimer from "@/components/MethodologyDisclaimer";
import JsonLd, { breadcrumbSchema } from "@/components/JsonLd";

export default function FoodPulseGuideDetail() {
  const { slug } = useParams();
  const [guide, setGuide] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await http.get(`/food-pulse/guides/${slug}`);
        if (!cancelled) setGuide(r.data);
      } catch {
        if (!cancelled) setNotFound(true);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-white">
        <FoodPulseTopNav />
        <FoodPulseJourneyNav />
        <div className="mx-auto max-w-2xl px-4 py-16 text-center" data-testid="fp-guide-not-found">
          <BookOpen className="mx-auto h-8 w-8 text-slate-300" />
          <h1 className="mt-3 font-serif text-2xl text-slate-900">Guide not found</h1>
          <p className="mt-2 text-sm text-slate-600">
            That cornerstone guide isn't here yet.
          </p>
          <Link
            to="/food-pulse/guides"
            className="mt-4 inline-flex items-center gap-1 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
          >
            <ArrowLeft className="h-3 w-3" /> Back to guides
          </Link>
        </div>
      </div>
    );
  }

  if (!guide) {
    return (
      <div className="min-h-screen bg-white">
        <FoodPulseTopNav />
        <FoodPulseJourneyNav />
        <div className="mx-auto max-w-2xl px-4 py-16 text-center text-sm text-slate-500">
          <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" /> Loading guide…
        </div>
      </div>
    );
  }

  const crumb = [
    { name: "FoodPulse", url: "https://foodpulse.net/food-pulse" },
    { name: "Guides",    url: "https://foodpulse.net/food-pulse/guides" },
    { name: guide.title, url: `https://foodpulse.net/food-pulse/guides/${guide.slug}` },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="fp-guide-detail">
      <SeoHead
        section="food-pulse"
        title={`${guide.title} — FoodPulse Guides`}
        description={guide.summary}
      />
      <JsonLd data={breadcrumbSchema(crumb)} />
      <FoodPulseTopNav />
      <FoodPulseJourneyNav active={guide.journey} />

      {guide.hero_image_url && (
        <div className="relative h-64 w-full bg-slate-100 sm:h-80" data-testid="fp-guide-hero">
          <img
            src={guide.hero_image_url}
            alt=""
            className="h-full w-full object-cover"
          />
        </div>
      )}

      <article className="mx-auto max-w-3xl px-4 py-10">
        <Link
          to="/food-pulse/guides"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-emerald-700 hover:underline"
          data-testid="fp-guide-back"
        >
          <ArrowLeft className="h-3 w-3" /> All guides
        </Link>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest text-emerald-800 ring-1 ring-emerald-200">
            {guide.journey}
          </span>
          <FeatureStatusBadge status={guide.status} />
        </div>
        <h1 className="mt-3 font-serif text-3xl text-slate-900 sm:text-4xl" data-testid="fp-guide-title">
          {guide.title}
        </h1>
        <p className="mt-3 text-base text-slate-600 sm:text-lg">
          {guide.summary}
        </p>

        {guide.author && (
          <Link
            to={`/food-pulse/contributors/${guide.author.slug}`}
            data-testid={`fp-guide-byline-${guide.author.slug}`}
            className="mt-4 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs text-emerald-900 hover:bg-emerald-100"
          >
            <BadgeCheck className="h-3.5 w-3.5 text-emerald-700" />
            <span>
              Reviewed by <b>{guide.author.name}</b>
              {guide.author.credentials
                ? ` · ${guide.author.credentials.split("·")[0].trim()}`
                : null}
            </span>
          </Link>
        )}

        <div className="mt-2 text-[11px] text-slate-500">
          Last reviewed <b>{guide.last_reviewed}</b>
          {" · "}
          <Link to="/food-pulse/corrections" className="text-emerald-700 hover:underline">
            corrections log
          </Link>
        </div>

        <div className="mt-8 space-y-6">
          {(guide.sections || []).map((s, i) => (
            <section key={i} data-testid={`fp-guide-section-${i}`}>
              <h2 className="font-serif text-xl text-slate-900">{s.heading}</h2>
              <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-slate-700">
                {s.body}
              </p>
            </section>
          ))}
        </div>

        {/* Sources — audit-required transparency */}
        {guide.sources?.length > 0 && (
          <div className="mt-8 rounded-xl border border-slate-100 bg-slate-50 p-4" data-testid="fp-guide-sources">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Sources</div>
            <ul className="mt-2 space-y-1 text-xs text-slate-700">
              {guide.sources.map((s, i) => (
                <li key={i} className="flex items-start gap-1"><ExternalLink className="mt-0.5 h-3 w-3 text-slate-400" /> {s}</li>
              ))}
            </ul>
          </div>
        )}

        <MethodologyDisclaimer
          kind="ai"
          className="mt-6"
          sources={guide.sources}
          lastReviewed={guide.last_reviewed}
          testid={`fp-guide-methodology-${guide.slug}`}
        />

        {guide.related?.length > 0 && (
          <div className="mt-8" data-testid="fp-guide-related">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Related guides</div>
            <ul className="mt-2 space-y-1 text-sm">
              {guide.related.map((r) => (
                <li key={r}>
                  <Link
                    to={`/food-pulse/guides/${r}`}
                    className="text-emerald-700 hover:underline"
                  >
                    → {r.replace(/-/g, " ")}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </article>
    </div>
  );
}
